import React from "react"
import tw, { css } from "twin.macro"
import { graphql } from "gatsby"
import PreHeader from "../atoms/pre-header"
import Sandbox from "../atoms/sandbox"
import ContentWrapper from "../atoms/content-wrapper"
import { GatsbyImage, getImage } from "gatsby-plugin-image"
import { themeStyles } from "../../styles/theme-helpers"

const childVariants = {
  in: {
    y: 0,
    opacity: 1,
  },
  out: {
    y: 40,
    opacity: 0,
  },
}

const TextContent = ({ data, headingXl, children }) => {
  const containerStyle = headingXl
    ? css`
        h3 {
          font-size: 3rem;
          line-height: 1;
        }
        h4 {
          font-size: 1.25rem;
          line-height: 1;
        }
        @media (min-width: 768px) {
          h3 {
            font-size: 4.6rem;
          }
          h4 {
            font-size: 2rem;
          }
        }
      `
    : null
  const theme = data.theme?.name || "white"
  const dotsCss = ["dark", "black"].includes(theme) ? "" : `bg-half-tone `
  return (
    <div
      tw="bg-white  relative px-3 sm:px-8 md:px-12 xl:px-16 xxl:px-24"
      className={`${dotsCss}tear-t-lg-${theme} tear-b-lg-${theme}`}
      css={[containerStyle, themeStyles[theme]]}
    >
      <div
        tw="mx-auto relative px-3 sm:px-8 lg:px-12 xl:px-16"
        css={[
          themeStyles[theme],
          theme !== "white"
            ? tw`py-8 sm:py-12 lg:py-14`
            : tw`py-6 sm:py-8 lg:py-10`,
        ]}
      >
        <ContentWrapper>
          {data.pre_header && (
            <PreHeader as="h5" variants={childVariants}>
              {data.pre_header}
            </PreHeader>
          )}
          {data.title && (
            <h3
              variants={childVariants}
              css={[
                tw`font-display text-4xl md:text-5xl xxl:text-6xl`,
                !data.subtitle && tw`mb-6`,
              ]}
            >
              {data.title}
            </h3>
          )}
          {data.subtitle && (
            <h4
              variants={childVariants}
              tw="font-bold mb-6 md:font-normal md:text-xl xl:mb-8"
            >
              {data.subtitle}
            </h4>
          )}
          {data.text_block &&
            data.text_block.map(tb => (
              <div key={tb.id}>
                {tb.title && (
                  <h4 tw="font-bold text-lg mb-4 mt-6 md:(text-xl mt-8)">
                    {tb.title}
                  </h4>
                )}
                <Sandbox
                  variants={childVariants}
                  tw="mb-4 xl:mb-6"
                  className={`text-columns--${tb.columns} text-size--${
                    tb.text_size || "base"
                  }`}
                  dangerouslySetInnerHTML={{ __html: tb.body }}
                ></Sandbox>
              </div>
            ))}
          {data.image_links?.length ? (
            <div tw="w-full pt-8 pb-6 px-6 flex items-center flex-wrap justify-center">
              {data.image_links.map(({ id, image, url, name }) => (
                <a
                  key={id}
                  href={url}
                  target="_BLANK"
                  rel="noreferrer"
                  title={name}
                  tw="block"
                >
                  <GatsbyImage
                    key={image.id}
                    image={getImage(image.localFile)}
                    alt={name || ""}
                  />
                </a>
              ))}
            </div>
          ) : (
            ""
          )}
          {children}
        </ContentWrapper>
      </div>
    </div>
  )
}

export const fragment = graphql`
  fragment TextContentFragment on DynamicZoneContainer {
    id
    theme {
      name
    }
    pre_header
    subtitle
    title
    text_block {
      id
      body
      title
      columns
      text_size
    }
    image_links {
      id
      url
      name
      image {
        localFile {
          childImageSharp {
            gatsbyImageData(
              width: 180
              formats: [AUTO, WEBP, AVIF]
              placeholder: TRACED_SVG
            )
          }
        }
        url
        id
      }
    }
  }
`

export default TextContent
